import { useCallback, useMemo } from 'react';

import { DEVICE_EVENT_UNKNOWN_HOSTNAME } from '~/modules/devices/constants/deviceScanEvent.const';
import { useDeviceEvents } from '~/modules/devices/hooks/useDeviceEvents';
import { type ScanEventProperties } from '~/modules/devices/types/deviceEventCatalog.type';
import { usePrinterConfig } from '~/modules/print/hooks/usePrinterConfig';
import { PrintAssetType } from '~/modules/print/types/print.type';

import { type ScanEvent } from '../types/scanEvent.type';
import { type ScanEventTracker } from '../types/scanEventTelemetry.type';
import { extractScanErrorInfo, toScanEventProperties } from '../utils/scanEventTelemetry.util';

export type UseScanDeviceEventsReturn = ScanEventTracker;

/**
 * Reports the outcome of the scan action handler as `device.scan.*` events.
 *
 * Failures are not noticed in New Relic here: the handler already reports every
 * failure path it takes, and reporting again would double-count them.
 */
export const useScanDeviceEvents = (): UseScanDeviceEventsReturn => {
  const { hostname, preferences } = usePrinterConfig();
  const printInitiation = preferences[PrintAssetType.ShippingLabel]?.initiation;
  const scanEvents = useDeviceEvents({ device: 'scan' });

  const staticContext = useMemo(
    () => ({
      hostname: hostname || DEVICE_EVENT_UNKNOWN_HOSTNAME,
      print_initiation: printInitiation,
    }),
    [hostname, printInitiation],
  );

  const buildEvent = useCallback(
    (scanEvent: ScanEvent, actionName?: string): ScanEventProperties => ({
      ...staticContext,
      ...toScanEventProperties(scanEvent, actionName),
    }),
    [staticContext],
  );

  const trackCompleted = useCallback(
    (scanEvent: ScanEvent, actionName: string): void => {
      scanEvents.track('completed', buildEvent(scanEvent, actionName));
    },
    [buildEvent, scanEvents],
  );

  const trackFailed = useCallback(
    (scanEvent: ScanEvent, error: unknown, actionName?: string): void => {
      const { errorCode, errorMessage } = extractScanErrorInfo(error);

      scanEvents.track('failed', {
        ...buildEvent(scanEvent, actionName),
        error_code: errorCode,
        error_message: errorMessage,
      });
    },
    [buildEvent, scanEvents],
  );

  return { trackCompleted, trackFailed };
};
